import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";

export interface SupportTicket {
  id: string;
  company_id: string;
  user_id: string;
  subject: string;
  body: string | null;
  priority: string;
  status: string;
  ticket_number: number | null;
  created_at: string;
  updated_at: string;
  company_name?: string;
}

export interface SupportMessage {
  id: string;
  ticket_id: string;
  sender_id: string;
  sender_type: "tenant" | "admin";
  body: string;
  created_at: string;
}

/** Tenant-side: fetch tickets for the current company */
export const useTenantTickets = () => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["support-tickets-tenant"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets" as any)
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as SupportTicket[];
    },
    staleTime: 30_000,
  });

  useEffect(() => {
    const channel = supabase
      .channel("tenant-support-tickets")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "support_tickets" },
        () => { qc.invalidateQueries({ queryKey: ["support-tickets-tenant"] }); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [qc]);

  return query;
};

/** Platform-side: fetch ALL tickets with company name */
export const usePlatformTickets = () => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["support-tickets-platform"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_tickets" as any)
        .select("*, companies(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return ((data ?? []) as any[]).map((t) => ({
        ...t,
        company_name: (t as any).companies?.name ?? null,
      })) as unknown as SupportTicket[];
    },
    staleTime: 15_000,
  });

  useEffect(() => {
    const channel = supabase
      .channel("platform-support-tickets")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "support_tickets" },
        () => { qc.invalidateQueries({ queryKey: ["support-tickets-platform"] }); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [qc]);

  return query;
};

/** Open ticket count for badge */
export const useOpenTicketCount = () => {
  const { data: tickets = [] } = usePlatformTickets();
  return tickets.filter((t) => t.status === "open").length;
};

/** Tenant creates a ticket */
export const useCreateTicket = () => {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: { subject: string; body: string; priority: string; companyId: string }) => {
      const { data, error } = await supabase
        .from("support_tickets" as any)
        .insert({
          company_id: params.companyId,
          user_id: user!.id,
          subject: params.subject,
          body: params.body,
          priority: params.priority,
        })
        .select("*")
        .single();
      if (error) throw error;
      return data as unknown as SupportTicket;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["support-tickets-tenant"] });
    },
  });
};

/** Fetch messages for a ticket with realtime */
export const useTicketMessages = (ticketId: string | null) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["support-ticket-messages", ticketId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_ticket_messages" as any)
        .select("*")
        .eq("ticket_id", ticketId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as SupportMessage[];
    },
    enabled: !!ticketId,
    staleTime: 10_000,
  });

  useEffect(() => {
    if (!ticketId) return;
    const channel = supabase
      .channel(`ticket-messages-${ticketId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "support_ticket_messages", filter: `ticket_id=eq.${ticketId}` },
        () => { qc.invalidateQueries({ queryKey: ["support-ticket-messages", ticketId] }); }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [qc, ticketId]);

  return query;
};

/** Send a message on a ticket (works for both tenant and admin) */
export const useSendTicketMessage = () => {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: { ticketId: string; body: string; senderType: "tenant" | "admin" }) => {
      const { error } = await supabase
        .from("support_ticket_messages" as any)
        .insert({
          ticket_id: params.ticketId,
          sender_id: user!.id,
          sender_type: params.senderType,
          body: params.body,
        });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["support-ticket-messages", vars.ticketId] });
    },
  });
};

/** Close a ticket */
export const useCloseTicket = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (ticketId: string) => {
      const { error } = await supabase
        .from("support_tickets" as any)
        .update({ status: "closed", updated_at: new Date().toISOString() })
        .eq("id", ticketId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["support-tickets-platform"] });
      qc.invalidateQueries({ queryKey: ["support-tickets-tenant"] });
    },
  });
};

/** Reopen a ticket */
export const useReopenTicket = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (ticketId: string) => {
      const { error } = await supabase
        .from("support_tickets" as any)
        .update({ status: "open", updated_at: new Date().toISOString() })
        .eq("id", ticketId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["support-tickets-platform"] });
      qc.invalidateQueries({ queryKey: ["support-tickets-tenant"] });
    },
  });
};
